/**
 * AuthGate — shell routing by session level and tenant status (FR-021;
 * contracts/session-and-gate.md "Frontend routing").
 *
 * One query, `['tenant']` → GET /v1/tenant, feeds the gate and every screen
 * (`useTenant()`). The map:
 *
 *   protect (default, wraps /app and the post-login auth routes)
 *     401 unauthorized            → /login
 *     403 mfa-required            → /mfa
 *     status pending_verification → /verify   (polls every 5 s in that state only)
 *     pending_subscription on /app/*, not /app/subscribe or /app/settings/data
 *                                 → /app/subscribe          (mission 9, OR-10; SC-36632)
 *     active on /app/subscribe without ?checkout=complete
 *                                 → /app/settings/billing   (mission 9, OR-10)
 *     otherwise                   → render    (read_only stays, any reason)
 *
 *   invert (wraps /login and /signup)
 *     full session, pending_subscription → /app/subscribe   (mission 9, OR-10)
 *     full session, verified      → APP_HOME
 *     full session, unverified    → /verify
 *     no or partial session       → render    (the auth screens own those states)
 *
 * Mission 9 WP11 T071 (FR-013; OR-10; squad U1, U26; research §1.28): a
 * pending tenant's /app pages are the subscribe page and the data page (the
 * server refuses the rest, amendment 7). The data page holds the privacy
 * controls, retention, export and deletion, which the server allows in every
 * state (billing_policy reasonPrivacy; D.6, FR-013). Without it a tenant that
 * never subscribed could not delete its account (SC-36632). An active tenant
 * has no subscribe page, except on
 * the checkout's return, `/app/subscribe?checkout=complete`, where
 * CheckoutReturn polls and navigates itself. A read-only tenant stays wherever
 * it is: the stored status carries no reason, so the page reads the reason from
 * the credits read's `billing` member (a cancelled plan may subscribe again; a
 * failed payment is answered by the 409 `dunning` row; analysis run 1, A3). The
 * stored status `cancelled` is D.6's deletion and never reaches the app.
 *
 *   /reset is mounted with no gate at all (routes.tsx): request mode needs
 *   none, and complete mode must work on a device that still holds a live
 *   session so the reset can end every session, that device's included (F18).
 *
 * When the target is the current path the children render, so /mfa and /verify
 * can sit behind the same gate without a redirect loop. Any other failure
 * (network, 5xx) renders a retryable blocked band in protect mode, never a
 * blank page and never the error string (§7.5; problem bodies are safe but
 * transport errors are not). In `invert` mode the same failure renders the
 * children: the login form must always be reachable, and its own submit
 * surfaces the failure.
 *
 * The gate renders nothing only before the first answer (`isFetched` false).
 * A refetch after a settled answer renders what that answer rendered: TanStack
 * treats a data-less query (the 401/403 that keeps a user on /mfa) as stale
 * and resets it to `pending`, error null, while it refetches, and a gate that
 * blanked on that frame would unmount the enrolment screen; on remount it
 * enrols again, which rotates the pending TOTP secret and invalidates a QR the
 * user may already have scanned, and the once-shown backup codes are gone. So
 * the children stay mounted through the refetch, and the blocked band stays up
 * through its own "Try again" rather than flashing the shell without a tenant.
 * For the same reason the query never refetches on window focus or reconnect
 * (a tab switch to a password manager or an authenticator is the normal
 * enrolment path); the /verify poll runs on `refetchInterval` and is
 * unaffected. (A.4, FR-011.)
 *
 * A second tab (FR-026; C2-1; TEN.1). Signing in as another tenant in a second
 * tab replaces the cookie under this one, and this tab's next tenant read
 * answers with a different tenant id while its cache still holds the first
 * tenant's queries. The gate records the last id it saw (a ref in the gate
 * instance, which lives under one QueryClientProvider: TanStack binds a
 * mounted query to its client). When a read answers with a different id it
 * records the new id first, drops every cached query (`clearSessionCache`),
 * and renders nothing, all in the render path: the children never render with
 * the new tenant over the old cache. The first id seen is not a change; the
 * comparison is on `id`, never the object, which structural sharing replaces
 * whenever any field changes. Mission 8 WP11 (FR-022; the mission 7 review
 * "Frontend residuals" row; R18): after the clear the gate seeds the tenant
 * query with the answer it just read, so no second `GET /v1/tenant` follows
 * the switch (a same-tab sign-in as another tenant read it twice), and it
 * re-renders once after the empty commit; the seeded id is the recorded one,
 * so that render settles with no loop.
 *
 * Two things the gate cannot do for the screens (WP10 owns both):
 *
 *   - Logout. `staleTime` is 30 s, so after a logout the cached full session
 *     would keep an `invert` gate on /login bouncing back to APP_HOME for up to
 *     30 s. `logout()` callers MUST call `clearTenantQuery(queryClient)` — i.e.
 *     `queryClient.removeQueries({ queryKey: TENANT_QUERY_KEY })` — before they
 *     navigate.
 *   - Leaving /verify. /verify is a protected route, not an inverted one, so
 *     once the 5 s poll sees the tenant leave `pending_verification` the gate
 *     just keeps rendering /verify. `Verify.tsx` navigates to APP_HOME itself
 *     when `useTenant().data?.status !== 'pending_verification'`.
 *
 * Nothing is mounted here; routes.tsx wires the gate around the /app layout
 * route (mission 6 T016) and around the auth routes.
 */
import { useEffect, useReducer, useRef, type ReactNode } from 'react'
import { queryOptions, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { ApiError } from '@/lib/api'
import { PROBLEM, getTenant, type Tenant } from '@/lib/auth'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

/** Where a fully authenticated, verified user lands from an auth route (sheet 10). Mission 6 T016. */
export const APP_HOME = '/app/runs'

/** Mission 9 (OR-10): a pending tenant's subscribe page (DATA_PATH is its other /app page), and the active tenant's billing page. */
export const SUBSCRIBE_PATH = '/app/subscribe'
export const BILLING_PATH = '/app/settings/billing'
/** The privacy controls (D.6, FR-013): reachable before a subscription (SC-36632). */
export const DATA_PATH = '/app/settings/data'

export const TENANT_QUERY_KEY = ['tenant'] as const

/** The waiting screen (07a) polls while the operator verifies the tenant; nothing else polls. */
export function tenantRefetchInterval(query: { state: { data?: Tenant | undefined } }): number | false {
  return query.state.data?.status === 'pending_verification' ? 5000 : false
}

export const tenantQueryOptions = queryOptions({
  queryKey: TENANT_QUERY_KEY,
  queryFn: getTenant,
  // A 401 or 403 is an answer, not a transient failure; never retry.
  retry: false,
  // Every screen mounts useTenant(); one fetch serves them all for 30 s.
  // Mutations that change the tenant invalidate TENANT_QUERY_KEY, and the
  // 5 s poll below ignores staleTime by design.
  staleTime: 30_000,
  refetchInterval: tenantRefetchInterval,
  // A data-less 401/403 counts as stale whatever staleTime says, so a focus or
  // reconnect refetch would reset it to pending and, without the isFetched
  // guard below, unmount /mfa mid-enrolment (rotating the pending secret).
  // Nothing on the auth routes changes the answer behind the user's back; the
  // /verify poll is refetchInterval and still runs.
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
})

export function useTenant() {
  return useQuery(tenantQueryOptions)
}

/**
 * Drop the cached tenant so the next gate evaluation refetches. Logout callers
 * call this before navigating: with `staleTime: 30_000` an `invert` gate on
 * /login would otherwise read the stale full session and bounce back to
 * APP_HOME for up to 30 s. `removeQueries`, not `invalidateQueries`, because a
 * logged-out client must not render the old organization even briefly.
 */
export function clearTenantQuery(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: TENANT_QUERY_KEY })
}

/**
 * Drop every cached query, the tenant's included (TEN.1 on the client;
 * mission 6 review follow-up 14, cycle 2 finding 1). Called when a session
 * ends (the session-expiry handler) and on every sign-in success before the
 * navigation (Login, and the MFA screen that completes a sign-in), so no
 * query of one tenant, such as `['credits']` with `staleTime: Infinity`,
 * `['runs', …]` or `['header-params']`, can render for the next. Sign out
 * already calls `queryClient.clear()` (AvatarMenu). It covers
 * `clearTenantQuery`'s obligation too.
 */
export function clearSessionCache(queryClient: QueryClient): void {
  queryClient.clear()
}

export interface GateInput {
  status: 'pending' | 'error' | 'success'
  error?: unknown
  data?: Tenant | undefined
}

/** Where the gate is: the router location's path and query. */
export interface GateLocation {
  pathname: string
  search: string
}

/** Mission 9 (OR-10, U1): the billing routing of a signed-in, verified tenant; `null` renders in place. */
function billingTarget(status: Tenant['status'], { pathname, search }: GateLocation): string | null {
  const onSubscribe = pathname === SUBSCRIBE_PATH
  const onApp = pathname === '/app' || pathname.startsWith('/app/')
  if (status === 'pending_subscription') return onApp && !onSubscribe && pathname !== DATA_PATH ? SUBSCRIBE_PATH : null
  if (status === 'active' && onSubscribe && new URLSearchParams(search).get('checkout') !== 'complete') return BILLING_PATH
  return null
}

/** Pure routing map; `null` means render in place. */
export function gateTarget(input: GateInput, invert: boolean, location: GateLocation = { pathname: '', search: '' }): string | null {
  if (input.status === 'success' && input.data) {
    if (input.data.status === 'pending_verification') return '/verify'
    if (invert) return input.data.status === 'pending_subscription' ? SUBSCRIBE_PATH : APP_HOME
    return billingTarget(input.data.status, location)
  }
  if (input.status === 'error') {
    const err = input.error
    if (invert) return null
    if (err instanceof ApiError) {
      if (err.problem.type === PROBLEM.UNAUTHORIZED || err.problem.status === 401) return '/login'
      if (err.problem.type === PROBLEM.MFA_REQUIRED) return '/mfa'
      if (err.problem.type === PROBLEM.VERIFICATION_REQUIRED) return '/verify'
    }
  }
  return null
}

export interface AuthGateProps {
  /** Auth-route mode: a full session leaves; no or partial session renders. */
  invert?: boolean
  /** Rendered when the gate passes; defaults to the nested route's `<Outlet />`. */
  children?: ReactNode
}

export function AuthGate({ invert = false, children }: AuthGateProps) {
  const { status, data, error, refetch, isFetched, isFetching } = useTenant()
  const queryClient = useQueryClient()
  const { pathname, search } = useLocation()
  // The last tenant id this gate saw (second tab; FR-026, C2-1, TEN.1).
  const seenTenantId = useRef<string | null>(null)
  // Set by a tenant switch: the seeded query changes nothing the gate reads, so it re-renders itself once after
  // the empty commit (R18).
  const switched = useRef(false)
  const [, rerender] = useReducer((n: number) => n + 1, 0)
  useEffect(() => {
    if (!switched.current) return
    switched.current = false
    rerender()
  })

  // The last settled failure. A refetch of a data-less query reports
  // `pending` with `error: null`, so this is the only way to keep rendering
  // what that answer rendered until the new one lands.
  const settledError = useRef<unknown>(undefined)
  if (status === 'error') settledError.current = error

  // A different tenant id: record it, drop every cached query, render nothing.
  // The rule (R18; FR-022, TEN.1): clearSessionCache stays unconditional, so
  // nothing of the previous tenant survives, and the only thing put back is the
  // tenant query, seeded with the answer just read (the new tenant's): the
  // observer rebuilds onto that fresh entry instead of reading GET /v1/tenant a
  // second time. Nothing renders in this frame; the effect above renders again.
  if (data) {
    const previous = seenTenantId.current
    seenTenantId.current = data.id
    if (previous !== null && previous !== data.id) {
      clearSessionCache(queryClient)
      queryClient.setQueryData(TENANT_QUERY_KEY, data)
      switched.current = true
      return null
    }
  }

  // Blank only before the first answer. isFetched (dataUpdateCount or
  // errorUpdateCount > 0) survives the pending reset; clearTenantQuery removes
  // the query, so a logout starts from blank again.
  if (status === 'pending' && !isFetched) return null
  const refetching = status === 'pending'
  const lastError = refetching ? settledError.current : error

  const target = gateTarget({ status, data, error }, invert, { pathname, search })
  if (target && target !== pathname) return <Navigate to={target} replace />

  // A transport error (not a problem body). Protect mode blocks with a retry,
  // and the band stays up while that retry is in flight; invert mode lets the
  // auth screen through so the login form is reachable.
  if ((status === 'error' || refetching) && lastError !== undefined && !(lastError instanceof ApiError) && !invert) {
    return (
      <Alert variant="blocked" className="m-8">
        <AlertTitle>Trellis could not load your organization</AlertTitle>
        <AlertDescription>
          <p>Check your connection and try again.</p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-3"
            aria-busy={isFetching || undefined}
            onClick={() => void refetch()}
          >
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  // A settled 401/403 on the route it points at (/mfa, /verify, an inverted
  // auth route), refetch in flight or not: the screen stays mounted, because
  // unmounting /mfa re-enrols on remount and rotates the pending secret.
  return <>{children ?? <Outlet />}</>
}
